import { useCallback, useEffect, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import clsx from 'clsx'

import LayoutContainer from 'components/Common/LayoutContainer'
import FilterChip from 'LuxKit/Chips/FilterChip'
import TextButton from 'LuxKit/Button/TextButton'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import {
  clearLocalExperiencesRecentSearches,
  deleteExperienceRecentSearch,
  getLocalExperiencesRecentSearches,
} from 'components/Experiences/hooks/useExperienceRecentSearches'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { clearRecentSearches, removeRecentSearch } from 'actions/SearchActions'
import useExperiences from 'hooks/Experiences/useExperiences'
import { arrayToMap, take } from 'lib/array/arrayUtils'
import ExperienceRecentlyViewedTile from 'components/Experiences/ExperienceTiles/ExperienceRecentlyViewedTile'
import ExperienceRecentlyViewedSkeletonTile from 'components/Experiences/ExperienceTiles/ExperienceRecentlyViewedSkeletonTile'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'

import ExperiencesHomepageRecentSearchTile from './ExperiencesHomepageRecentSearchTile'
import {
  getExperiencesHomepageSuggestedSearches,
  type ExperiencesHomepageSuggestedSearchView,
} from 'lib/experiences/experiencesHomepageUtils'
import variables from 'styles/vendor/bootstrap/variables'

import { getExperiencesHomepageUpliftListName, useExperiencesHomepageUpliftTracking } from './experiencesHomepageUpliftAnalytics'

type RecentSearchesTab = 'suggestedSearches' | 'previouslyViewed'

interface TabOption {
  id: RecentSearchesTab
  title: string
}

interface Props {
  excludedBookingPlaceIds?: Array<string>
  excludedBookingPlaceNames?: Array<string>
}

const Chips = styled(Group)`
  flex: 1 1 auto;
  min-inline-size: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  margin-inline: -${rem(variables.gutterWidth / 2)};
  padding-inline: ${rem(20)};

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }

  ${mediaQueryUp.tablet} {
    overflow-x: visible;
    margin-inline: 0;
    padding-inline: 0;
  }
`

const ClearAllDesktop = styled.div`
  display: none;
  flex: 0 0 auto;
  margin-inline-start: auto;
  visibility: hidden;
  pointer-events: none;

  &.visible {
    visibility: visible;
    pointer-events: auto;
  }

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const ViewedTileFrame = styled.div`
  inline-size: 100%;

  > * {
    box-sizing: border-box;
    inline-size: 100%;
  }
`

const RecentSearchesCarousel = styled(CardCarousel)`
  min-block-size: ${rem(88)};

  ${mediaQueryUp.tablet} {
    min-block-size: ${rem(96)};

    [data-testid='experiences-homepage-recent-searches-carousel'] {
      --carousel-gap: ${rem(24)} !important;
    }
  }
`

const PREVIOUSLY_VIEWED_LIMIT = 5

function ExperiencesHomepageRecentSearches({
  excludedBookingPlaceIds,
  excludedBookingPlaceNames,
}: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const globalRecentSearches = useRecentSearches()
  const [experienceRecentSearches, setExperienceRecentSearches] = useState(getLocalExperiencesRecentSearches)
  const experienceCategories = useAppSelector(state => state.experience.experienceCategories)
  const recentlyViewedExperienceIds = useAppSelector(state => state.experience.recentlyViewedExperienceIds)
  const trackSuggestedSearchesInteraction = useExperiencesHomepageUpliftTracking('suggested_searches')
  const previouslyViewedTracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', getExperiencesHomepageUpliftListName('previously_viewed')),
  )

  const [selectedTab, setSelectedTab] = useState<RecentSearchesTab | undefined>()

  const suggestedSearches = useMemo(
    () => getExperiencesHomepageSuggestedSearches(intl, {
      globalRecentSearches,
      experienceRecentSearches,
      excludedPlaceIds: excludedBookingPlaceIds,
      excludedPlaceNames: excludedBookingPlaceNames,
      experienceCategories,
    }),
    [
      intl,
      globalRecentSearches,
      experienceRecentSearches,
      excludedBookingPlaceIds,
      excludedBookingPlaceNames,
      experienceCategories,
    ],
  )

  const viewedIds = useMemo(
    () => take(recentlyViewedExperienceIds, PREVIOUSLY_VIEWED_LIMIT),
    [recentlyViewedExperienceIds],
  )
  const [viewedExperiences, isFetchingViewed] = useExperiences(viewedIds)
  const viewedById = useMemo(() => arrayToMap(viewedExperiences, exp => exp.id), [viewedExperiences])

  const availableViewedIds = useMemo(() => {
    if (isFetchingViewed) {
      return viewedIds
    }

    return viewedIds.filter(id => viewedById.has(id))
  }, [isFetchingViewed, viewedById, viewedIds])

  const hasSuggested = suggestedSearches.length > 0
  const hasPreviouslyViewed = recentlyViewedExperienceIds.length > 0 && (isFetchingViewed || availableViewedIds.length > 0)

  const tabOptions = useMemo(() => {
    const options: Array<TabOption> = []

    if (hasPreviouslyViewed) {
      options.push({
        id: 'previouslyViewed',
        title: intl.formatMessage({
          id: 'ddPla_h88NsnSeIncm--',
          defaultMessage: 'Previously viewed offers',
          description: 'Filter chip label for previously viewed experience offers tab on Experiences homepage uplift',
        }),
      })
    }

    if (hasSuggested) {
      options.push({
        id: 'suggestedSearches',
        title: intl.formatMessage({
          id: 'pUsR_doh0BUYXLGSRW3e',
          defaultMessage: 'Suggested searches',
          description: 'Filter chip label for suggested searches tab on Experiences homepage uplift recent searches section',
        }),
      })
    }

    return options
  }, [hasSuggested, hasPreviouslyViewed, intl])

  useEffect(() => {
    if (!tabOptions.length) {
      setSelectedTab(undefined)
      return
    }

    if (!selectedTab || !tabOptions.some(option => option.id === selectedTab)) {
      setSelectedTab(tabOptions[0].id)
    }
  }, [tabOptions, selectedTab])

  const onTabClick = useCallback((tabId: RecentSearchesTab) => {
    setSelectedTab(tabId)
    if (tabId === 'suggestedSearches') {
      trackSuggestedSearchesInteraction('suggested_searches', 'tab_selected')
    }
  }, [trackSuggestedSearchesInteraction])

  const onDismissSuggested = useCallback((item: ExperiencesHomepageSuggestedSearchView) => {
    if (item.source === 'experience') {
      deleteExperienceRecentSearch(item.recentSearch)
      setExperienceRecentSearches(getLocalExperiencesRecentSearches())
      return
    }

    dispatch(removeRecentSearch(item.recentSearch.recentSearchId))
  }, [dispatch])

  const onClearAll = useCallback(() => {
    trackSuggestedSearchesInteraction('suggested_searches', 'clear_all_clicked')
    dispatch(clearRecentSearches())
    clearLocalExperiencesRecentSearches()
    setExperienceRecentSearches(getLocalExperiencesRecentSearches())
  }, [dispatch, trackSuggestedSearchesInteraction])

  if (!tabOptions.length) {
    return null
  }

  const showClearAll = selectedTab === 'suggestedSearches' && hasSuggested

  return (
    <LayoutContainer>
      <Group direction="vertical" gap={24}>
        <Group direction="horizontal" verticalAlign="center" gap={8} fullWidth>
          <Chips direction="horizontal" gap={8} wrap="nowrap">
            {tabOptions.map((option) => (
              <FilterChip
                key={option.id}
                selected={option.id === selectedTab}
                onClick={() => onTabClick(option.id)}
                size="medium"
              >
                {option.title}
              </FilterChip>
            ))}
          </Chips>
          <ClearAllDesktop className={clsx({ visible: showClearAll })}>
            <TextButton
              kind="tertiary"
              size="medium"
              horizontalOutdent="end"
              onClick={onClearAll}
              tabIndex={showClearAll ? undefined : -1}
              aria-hidden={!showClearAll}
            >
              {intl.formatMessage({
                id: 'ijci_I5ituYLBg-AT9xO',
                defaultMessage: 'Clear all',
                description: 'Desktop action to clear all suggested recent searches on Experiences homepage uplift',
              })}
            </TextButton>
          </ClearAllDesktop>
        </Group>

        {selectedTab === 'suggestedSearches' && (
          <RecentSearchesCarousel
            pageSize="auto"
            width={rem(300)}
            tabletWidth={rem(366)}
            gap={16}
            gutterSize={0}
            desktopGutterSize={0}
            snap="start"
            data-testid="experiences-homepage-recent-searches-carousel"
          >
            {suggestedSearches.map((item, index) => (
              <ExperiencesHomepageRecentSearchTile
                key={item.recentSearch.recentSearchId}
                item={item}
                position={index + 1}
                onDismiss={onDismissSuggested}
              />
            ))}
          </RecentSearchesCarousel>
        )}

        {selectedTab === 'previouslyViewed' && (
          <OfferListEventsProvider tracking={previouslyViewedTracking}>
            <RecentSearchesCarousel
              pageSize="auto"
              width={rem(300)}
              tabletWidth={rem(366)}
              gap={16}
              gutterSize={0}
              desktopGutterSize={0}
              snap="start"
              data-testid="experiences-homepage-recent-searches-carousel"
            >
              {availableViewedIds.map((id, index) => {
                const experience = viewedById.get(id)
                return (
                  <ViewedTileFrame key={id}>
                    {!experience && <ExperienceRecentlyViewedSkeletonTile />}
                    {experience && (
                      <ExperienceRecentlyViewedTile
                        experience={experience}
                        position={index}
                      />
                    )}
                  </ViewedTileFrame>
                )
              })}
            </RecentSearchesCarousel>
          </OfferListEventsProvider>
        )}
      </Group>
    </LayoutContainer>
  )
}

export default ExperiencesHomepageRecentSearches
